/* ═══════════════════════════════════════════════════════
   suite-io — Doc Context
   ═══════════════════════════════════════════════════════
   Manages the docs collection + content blocks. Slim port of legacy
   nyc-suite's DocContext: the slices the Jobs app's Docs & Files tab
   needs today (generated-docs list + the template picker's blocks).
   The legacy extras (docTemplates, docFolders, block version CRUD)
   come over with the doc-builder / general document library.
*/

import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { dataService } from "../lib/dataService.js";
import { useCollectionState } from "./useCollectionState.js";
import notify from "../lib/notify.jsx";

const DocContext = createContext(null);

// `enabled` gates ALL Firestore traffic: docs are an admin surface (generated
// client docs, billing agreements), so crew/viewer sessions must not download
// the collection at all. Disabled → empty data, no load, no subscriptions.
export function DocProvider({ children, enabled = true }) {
  const docs = useCollectionState([]);
  const blocks = useCollectionState([]);
  const [loaded, setLoaded] = useState(false);

  /* ═══ INITIAL LOAD ═══ */
  useEffect(() => {
    if (!enabled) {
      docs.setLoading(false);
      blocks.setLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const [docsData, blocksData] = await Promise.all([
          dataService.docs.loadAll(),
          dataService.contentBlocks.loadAll(),
        ]);
        if (!mounted) return;
        docs.setData(docsData);
        docs.setLoading(false);
        blocks.setData(blocksData);
        blocks.setLoading(false);
        setLoaded(true);
      } catch (e) {
        console.error("DocProvider load error:", e);
        docs.setError(e.message);
        docs.setLoading(false);
        setLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, [enabled]);

  /* ═══ REAL-TIME LISTENERS ═══ */
  useEffect(() => {
    if (!loaded || !enabled) return;
    return dataService.docs.subscribe((data, meta) => {
      if (meta.hasPendingWrites) return;
      docs.setData(data);
    });
  }, [loaded, enabled]);

  useEffect(() => {
    if (!loaded || !enabled) return;
    return dataService.contentBlocks.subscribe((data, meta) => {
      if (meta.hasPendingWrites) return;
      blocks.setData(data);
    });
  }, [loaded, enabled]);

  /* ═══ DOC CRUD ═══ */
  const saveDoc = useCallback(async (docObj) => {
    const d = { ...docObj, updatedAt: new Date().toISOString() };
    const snapshot = docs.data;
    docs.setData(prev => {
      const idx = prev.findIndex(x => x.id === d.id);
      return idx >= 0 ? prev.map(x => x.id === d.id ? d : x) : [...prev, d];
    });
    try {
      await dataService.docs.save(d);
    } catch (e) {
      docs.setData(snapshot);
      notify.error("Failed to save document. Please try again.");
    }
  }, [docs.data]);

  const deleteDoc = useCallback(async (docId) => {
    const snapshot = docs.data;
    docs.setData(prev => prev.filter(x => x.id !== docId));
    try { await dataService.docs.delete(docId); }
    catch (e) { console.error("deleteDoc:", e); docs.setData(snapshot); }
  }, [docs.data]);

  const value = {
    savedDocs: docs.data,
    docsLoading: docs.loading,
    docsError: docs.error,
    saveDoc,
    deleteDoc,
    contentBlocks: blocks.data,
  };

  return <DocContext.Provider value={value}>{children}</DocContext.Provider>;
}

export function useDoc() {
  const ctx = useContext(DocContext);
  if (!ctx) throw new Error("useDoc must be used within DocProvider");
  return ctx;
}

export function DocContextTestProvider({ value, children }) {
  return <DocContext.Provider value={value}>{children}</DocContext.Provider>;
}
